import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';

const SplashScreen = ({ onFinish }) => {
  const TOTAL_DURATION_MS = 5000; // 5 seconds total
  const LOADING_DURATION_MS = 4100; // 4.1s loading, 0.3s hold, 0.6s fade-out = exactly 5s

  const [progress, setProgress] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const currentProgress = Math.min(100, Math.floor((elapsed / LOADING_DURATION_MS) * 100));
      setProgress(currentProgress);

      if (elapsed >= LOADING_DURATION_MS) {
        clearInterval(interval);
        setProgress(100);
      }
    }, 40);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (progress >= 100) {
      const timer = setTimeout(() => {
        setIsFadingOut(true);
        const finishTimer = setTimeout(() => {
          if (onFinish) onFinish();
        }, 600);
        return () => clearTimeout(finishTimer);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [progress, onFinish]);

  const handleSkip = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      if (onFinish) onFinish();
    }, 400);
  };

  return (
    <div
      className={`fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-[#030812] text-white select-none overflow-hidden transition-all duration-700 ease-in-out ${
        isFadingOut ? 'opacity-0 scale-105 pointer-events-none' : 'opacity-100 scale-100 pointer-events-auto'
      }`}
    >
      {/* Ambient Gradient Background Glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-36 -left-36 w-[450px] h-[450px] bg-emerald-500/20 rounded-full blur-[120px] animate-pulse"></div>
        <div
          className="absolute -bottom-36 -right-36 w-[450px] h-[450px] bg-teal-500/20 rounded-full blur-[120px] animate-pulse"
          style={{ animationDuration: '4s' }}
        ></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[550px] h-[550px] bg-emerald-600/10 rounded-full blur-[140px]"></div>

        {/* Subtle grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '36px 36px',
          }}
        ></div>
      </div>

      {/* Top Skip Button */}
      <div className="absolute top-6 right-6 z-20">
        <button
          onClick={handleSkip}
          className="group flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white/10 hover:bg-emerald-500/20 border border-white/10 backdrop-blur-md text-xs font-semibold text-emerald-200 hover:text-white transition-all duration-200 active:scale-95"
        >
          <span>Skip</span>
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* Main Center Content: Only Logo + Sai Medical */}
      <div className="relative z-10 flex flex-col items-center max-w-sm sm:max-w-md w-full px-6 text-center">
        
        {/* Authentic Medical Logo Emblem */}
        <div className="relative flex items-center justify-center mb-6">
          {/* Pulsing Concentric Rings */}
          <div className="absolute w-44 h-44 sm:w-52 sm:h-52 rounded-full border border-emerald-500/25 animate-ping" style={{ animationDuration: '3s' }}></div>
          <div className="absolute w-56 h-56 sm:w-64 sm:h-64 rounded-full border border-teal-500/15 animate-pulse" style={{ animationDuration: '4s' }}></div>
          <div className="absolute -inset-4 bg-gradient-to-tr from-emerald-500 via-teal-400 to-cyan-500 rounded-full blur-2xl opacity-40 animate-pulse"></div>

          {/* Central Medical Logo */}
          <div className="relative w-32 h-32 sm:w-36 sm:h-36 flex items-center justify-center transform hover:scale-105 transition-transform duration-500">
            <svg
              className="w-full h-full drop-shadow-[0_0_35px_rgba(16,185,129,0.55)]"
              viewBox="0 0 120 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                {/* 3D Medical Cross Gradient */}
                <linearGradient id="medCrossGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#34D399" />
                  <stop offset="40%" stopColor="#10B981" />
                  <stop offset="100%" stopColor="#047857" />
                </linearGradient>
                {/* Cross Inner Bevel */}
                <linearGradient id="crossBevel" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#064E3B" stopOpacity="0.2" />
                </linearGradient>
                {/* Golden Rx and Caduceus Accent */}
                <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FDE68A" />
                  <stop offset="50%" stopColor="#F59E0B" />
                  <stop offset="100%" stopColor="#D97706" />
                </linearGradient>
                {/* Shield Dark Glow */}
                <radialGradient id="shieldBg" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#0B2E24" />
                  <stop offset="85%" stopColor="#041612" />
                  <stop offset="100%" stopColor="#020C0A" />
                </radialGradient>
              </defs>

              {/* Circular Medical Shield Backdrop */}
              <circle cx="60" cy="60" r="57" fill="url(#shieldBg)" stroke="#10B981" strokeWidth="2.5" strokeOpacity="0.7" />
              <circle cx="60" cy="60" r="52" fill="none" stroke="#34D399" strokeWidth="1" strokeDasharray="4 3" strokeOpacity="0.4" />

              {/* Classic Medical Cross - Vertical Bar */}
              <rect x="46" y="18" width="28" height="84" rx="7" fill="url(#medCrossGrad)" />
              {/* Classic Medical Cross - Horizontal Bar */}
              <rect x="18" y="46" width="84" height="28" rx="7" fill="url(#medCrossGrad)" />

              {/* Glossy Emboss Highlights on Cross */}
              <rect x="47.5" y="19.5" width="25" height="81" rx="5.5" fill="none" stroke="url(#crossBevel)" strokeWidth="1.5" />
              <rect x="19.5" y="47.5" width="81" height="25" rx="5.5" fill="none" stroke="url(#crossBevel)" strokeWidth="1.5" />

              {/* Clean Crisp Inner White Medical Cross */}
              <rect x="52.5" y="25" width="15" height="70" rx="3.5" fill="white" fillOpacity="0.95" />
              <rect x="25" y="52.5" width="70" height="15" rx="3.5" fill="white" fillOpacity="0.95" />

              {/* Rod of Asclepius (Staff & Serpent) */}
              <path d="M60 26 V74" stroke="url(#goldGrad)" strokeWidth="3" strokeLinecap="round" />
              <circle cx="60" cy="24" r="3.5" fill="url(#goldGrad)" />
              <path
                d="M55 33 C55 29, 65 29, 65 33 C65 38, 55 39, 55 45 C55 51, 65 52, 65 58 C65 64, 55 65, 55 70"
                stroke="url(#goldGrad)"
                strokeWidth="2.5"
                strokeLinecap="round"
                fill="none"
              />

              {/* Medical Heartbeat Wave Pulse */}
              <path
                d="M27 60 H42 L47 47 L53 71 L59 50 L64 66 L69 60 H85"
                stroke="#047857"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Official ℞ (Rx Prescription) Pharmacy Emblem */}
              <g transform="translate(73, 15)">
                <circle cx="13" cy="13" r="12" fill="#061A14" stroke="#F59E0B" strokeWidth="1.5" />
                <text x="13" y="18" fill="#FDE68A" fontSize="13" fontWeight="900" fontFamily="serif" textAnchor="middle">
                  ℞
                </text>
              </g>
            </svg>
          </div>
        </div>

        {/* Title: Only "Sai Medical" */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-black tracking-wider bg-gradient-to-r from-white via-emerald-100 to-teal-300 bg-clip-text text-transparent drop-shadow-md">
            Sai Medical
          </h1>
        </div>

        {/* Minimal 10-Second Glowing Progress Bar */}
        <div className="w-full max-w-xs">
          <div className="w-full h-1.5 rounded-full bg-slate-900/90 p-0.5 border border-emerald-900/40 overflow-hidden shadow-inner">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-400 transition-all duration-150 ease-out shadow-[0_0_12px_rgba(52,211,153,0.8)] relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-white/30 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SplashScreen;
